import { CheckCircle2, ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";
import Centered from "./Centered";

/**
 * Single multiplexed terminal screen — handles every "after the chat is
 * done" UI state so MicroResearch.jsx itself doesn't have to. Switches
 * on the `phase` plus the backend-returned `finalScreen.screen`:
 *
 *   - filled                 → "this survey is filled"
 *   - already_participated   → "you've already participated"
 *   - screened_out           → screener-knockout exit
 *   - terminated             → fraud-flagged session
 *   - review                 → submission under manual review
 *   - finished               → thank-you (reward / pickfu / branded)
 */
export default function ScreenFinal({
  phase, campaign, finalScreen, hideReward, lang, tr, nav,
}) {
  if (phase === "filled") {
    return (
      <Centered>
        <div className="text-center max-w-md" data-testid="quota-filled-screen">
          <CheckCircle2 className="w-12 h-12 text-[var(--rl-success)] mx-auto" />
          <h1 className="font-display font-black text-3xl mt-6">This survey has been filled.</h1>
          <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">Thanks for showing up. Another study is likely matching you very soon.</p>
          <button onClick={() => nav("/")} className="mt-6 bg-[var(--rl-text)] text-white px-5 py-3 font-semibold hover:bg-[var(--rl-brand)] transition-colors" data-testid="filled-home-btn">Return home</button>
        </div>
      </Centered>
    );
  }

  if (phase === "already_participated") {
    return (
      <Centered>
        <div className="text-center max-w-md" data-testid="already-participated-screen">
          <ShieldCheck className="w-12 h-12 text-[var(--rl-brand)] mx-auto" />
          <h1 className="font-display font-black text-3xl mt-6" data-testid="already-participated-title">You have already participated in this research campaign.</h1>
          <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">Each panelist can only respond once per study so the results stay clean for our client. Thanks for showing up — another study is likely matching you very soon.</p>
          <button onClick={() => nav("/")} className="mt-6 bg-[var(--rl-text)] text-white px-5 py-3 font-semibold hover:bg-[var(--rl-brand)] transition-colors" data-testid="already-participated-home-btn">Return home</button>
        </div>
      </Centered>
    );
  }

  if (phase === "screened_out") {
    return (
      <Centered>
        <div className="text-center max-w-md" data-testid="screened-out-screen">
          <ShieldCheck className="w-12 h-12 text-[var(--rl-text-secondary)] mx-auto" />
          <h1 className="font-display font-black text-3xl mt-6">{tr("screener_dq_title")}</h1>
          <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">{tr("screener_dq_body")}</p>
          <button onClick={() => nav("/")} className="mt-6 bg-[var(--rl-text)] text-white px-5 py-3 font-semibold hover:bg-[var(--rl-brand)] transition-colors" data-testid="screened-out-home-btn">{lang === "fr" ? "Retour à l'accueil" : "Return home"}</button>
        </div>
      </Centered>
    );
  }

  if (phase === "terminated") {
    return (
      <Centered>
        <div className="text-center max-w-md" data-testid="terminated-screen">
          <AlertTriangle className="w-10 h-10 text-[var(--rl-accent)] mx-auto" />
          <h1 className="font-display font-black text-3xl mt-5">Session ended.</h1>
          <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">Our quality layer flagged your responses. You will not be eligible for this study.</p>
          <button onClick={() => nav("/")} className="mt-6 bg-[var(--rl-text)] text-white px-5 py-3 font-semibold">Return home</button>
        </div>
      </Centered>
    );
  }

  if (phase === "review") {
    return (
      <Centered>
        <div className="text-center max-w-md" data-testid="review-screen">
          <Loader2 className="w-8 h-8 text-[var(--rl-brand)] mx-auto" />
          <h1 className="font-display font-black text-3xl mt-5">Submission under review.</h1>
          <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">Thanks for your time. Our team will review your interview shortly.</p>
        </div>
      </Centered>
    );
  }

  // phase === "finished" — branch on backend-returned screen variant.
  if (finalScreen?.screen === "thank_you_internal") {
    return (
      <Centered>
        <div className="text-center max-w-md" data-testid="thank-you-reward-screen">
          <CheckCircle2 className="w-12 h-12 text-[var(--rl-success)] mx-auto" />
          <h1 className="font-display font-black text-3xl mt-6">All done!</h1>
          <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">Thanks for your time. Your responses have been recorded and will shape what {campaign?.title || "we"} build next.</p>
        </div>
      </Centered>
    );
  }
  if (finalScreen?.screen === "thank_you_cloudresearch") {
    return (
      <Centered>
        <div className="text-center max-w-md" data-testid="thank-you-cloudresearch-screen">
          <CheckCircle2 className="w-12 h-12 text-[var(--rl-success)] mx-auto" />
          <h1 className="font-display font-black text-3xl mt-6">{tr("thanks_pickfu_title") || "Returning you to the panel…"}</h1>
          <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">Your responses have been recorded. The panel network will settle your reward shortly.</p>
        </div>
      </Centered>
    );
  }
  return (
    <Centered>
      <div className="text-center max-w-md" data-testid="thank-you-branded-screen">
        <CheckCircle2 className="w-12 h-12 text-[var(--rl-success)] mx-auto" />
        <h1 className="font-display font-black text-3xl mt-6">Thank you from {(finalScreen?.company || "our team").replace(/\.+$/, "")}.</h1>
        <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">Your feedback shapes what we build next.</p>
        {finalScreen?.website && (
          <a href={finalScreen.website} target="_blank" rel="noreferrer" className="inline-block mt-6 text-[var(--rl-brand)] font-semibold">Visit our site →</a>
        )}
      </div>
    </Centered>
  );
}
